import { createSlice } from "@reduxjs/toolkit";
import { toast } from "react-toastify";

/** ui.activeRequests: user clicked Sync, waiting for server. */
export const SYNC_SEAMLESS_ACTIVE_REQUEST = "sync_seamless";
/** ui.activeRequests: server reported sync started. */
export const SYNC_SEAMLESS_STARTED_REQUEST = "sync_seamless_started";
/** ui.activeRequests: user clicked Schwab SMA sync (outgoing sma_sync). */
export const SYNC_SMA_ACTIVE_REQUEST = "sma_sync";
/** ui.activeRequests: server reported Schwab SMA sync started (incoming sync_sma). */
export const SYNC_SMA_STARTED_REQUEST = "sync_sma_started";

const initialState = {
  accountingData: [],
  loading: false,
  error: null,
};

const accountingSlice = createSlice({
  name: "accounting",
  initialState,
  reducers: {
    setAccountingData: (state, action) => {
      state.accountingData = action.payload;
      state.loading = false;
      state.error = null;
    },
    updateAccountingAum: (state, action) => {
      const { isSMA, accountingItem } = action.payload;
      
      if (!accountingItem || !state.accountingData[0]?.funds) {
        toast.error("Failed to update Accounting AUM");
        state.loading = false;
        state.error = null;
        return;
      }

      const { aum } = accountingItem;
      const entityId = isSMA ? accountingItem.sma : accountingItem.fund;
      
      if (!entityId) {
        toast.error("Fund ID not found");
        state.loading = false;
        state.error = null;
        return;
      }

      // Find and update the entity
      const entityToUpdate = state.accountingData[0].funds.find(
        (item) => item.fund === entityId
      );

      if (entityToUpdate) {
        entityToUpdate.aum = aum;
        
        // Only update strategyAUMs for funds, not SMAs
        if (!isSMA && accountingItem.strategyAUMs) {
          entityToUpdate.strategyAUMs = accountingItem.strategyAUMs;
        }
        
        toast.success(`${isSMA ? 'SMA' : 'Fund'} AUM updated successfully`);
      } else {
        toast.error(`${isSMA ? 'SMA' : 'Fund'} not found in accounting data`);
      }
      
      state.loading = false;
      state.error = null;
    },
    addAccountingAum: (state, action) => {
      const { isSMA, accountingItem } = action.payload;
      
      if (!accountingItem || !state.accountingData[0]?.funds) {
        toast.error("Failed to add Accounting AUM");
        state.loading = false;
        state.error = null;
        return;
      }

      const { accountingId, aum, date, managerId } = accountingItem;
      const entityId = isSMA ? accountingItem.sma : accountingItem.fund;
      
      if (!entityId) {
        toast.error("Fund ID not found");
        state.loading = false;
        state.error = null;
        return;
      }

      // Check if entity already exists
      const existingEntity = state.accountingData[0].funds.find(
        (item) => item.fund === entityId
      );

      if (existingEntity) {
        // Update existing entity's AUM
        existingEntity.aum = aum;
        toast.success(`${isSMA ? 'SMA' : 'Fund'} AUM updated successfully`);
      } else {
        // Add new entity
        const newEntity = {
          fund: entityId,
          aum: aum,
          managerId: managerId,
          accountingId: accountingId,
          date: date,
          isSMA: isSMA
        };

        // Only add strategyAUMs for funds, not SMAs
        if (!isSMA) {
          newEntity.strategyAUMs = [];
        }

        state.accountingData[0].funds.push(newEntity);
        toast.success(`${isSMA ? 'SMA' : 'Fund'} added successfully`);
      }

      state.loading = false;
      state.error = null;
    },
  
    setAccountingLoading: (state, action) => {
      state.loading = action.payload;
    },
    setAccountingError: (state, action) => {
      state.error = action.payload;
      state.loading = false;
    },
    clearAccountingError: (state) => {
      state.error = null;
    },
    // Update the top-level accounting sync status (e.g. from sync_seamless websocket messages)
    updateAccountingSyncStatus: (state, action) => {
      const { status, timestamp } = action.payload || {};

      if (typeof status === "string" && status.toLowerCase() === "completed") {
        toast.success("Accounting data synchronized successfully.", {
          autoClose: 4000,
        });
      }

      if (!state.accountingData || !state.accountingData[0]) return;

      const root = state.accountingData[0];
      if (!root.sync) {
        root.sync = {};
      }

      if (typeof status === "string") {
        const normalized = status.toLowerCase();
        const statusToStage = {
          completed: 0,
          downloading: 1,
          processing: 2,
          started: 3,
        };
        const stage = statusToStage[normalized];
        if (stage !== undefined) {
          root.sync.stage = stage;
        }
      }

      const effectiveTimestamp =
        timestamp !== undefined && timestamp !== null
          ? String(timestamp)
          : String(Date.now());

      root.sync.date = effectiveTimestamp;
    },
  },
});

export const {
  setAccountingData,
  updateAccountingAum,
  addAccountingAum,
  setAccountingLoading,
  setAccountingError,
  clearAccountingError,
  updateAccountingSyncStatus,
} = accountingSlice.actions;

// Selectors - Direct access to accounting state
export const selectAccountingData = (state) => state.accounting?.accountingData || [];
export const selectAccountingLoading = (state) => state.accounting?.loading || false;
export const selectAccountingError = (state) => state.accounting?.error || null;

export default accountingSlice.reducer;